import { useEffect, useRef, useState, type ReactNode } from 'react';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { siteMasterLoginUrl } from '@/lib/siteMasterPaths';
import { cn } from '@/lib/cn';

export const SITE_MASTER_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const SITE_MASTER_IMAGE_MAX_COUNT = 9;

const fieldClass =
  'w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-lg focus:border-teal-500 focus:outline-none focus:ring-4 focus:ring-teal-500/20';
const labelClass = 'mb-2 block text-lg font-semibold text-slate-700';

export type SiteMasterEntryTypeOption = { key: string; label: string };

export type SiteMasterEntryFormValues = {
  site_id?: number;
  type_key: string;
  body_text: string;
  contact_phone: string;
  contact_wechat_id: string;
  image_local_paths: string[];
};

type SiteOption = { id: number; site_name: string };

type Props = {
  siteId: number;
  siteOptions?: SiteOption[];
  initialTypeKey?: string;
  initialBody?: string;
  initialPhone?: string;
  initialWechatId?: string;
  initialImagePaths?: string[];
  submitLabel: string;
  saving?: boolean;
  onSubmit: (values: SiteMasterEntryFormValues) => void | Promise<void>;
  footer?: ReactNode;
};

function wechatIdLooksValid(raw: string): boolean {
  const s = raw.trim();
  if (s === '') return true;
  return s.length >= 4 && s.length <= 32 && /^[\w-]+$/.test(s);
}

export function SiteMasterEntryForm({
  siteId,
  siteOptions,
  initialTypeKey = '',
  initialBody = '',
  initialPhone = '',
  initialWechatId = '',
  initialImagePaths = [],
  submitLabel,
  saving = false,
  onSubmit,
  footer,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSiteId, setSelectedSiteId] = useState(siteId);
  const [typeKey, setTypeKey] = useState(initialTypeKey);
  const [typeOptions, setTypeOptions] = useState<SiteMasterEntryTypeOption[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [body, setBody] = useState(initialBody);
  const [phone, setPhone] = useState(initialPhone);
  const [wechatId, setWechatId] = useState(initialWechatId);
  const [imagePaths, setImagePaths] = useState<string[]>(initialImagePaths);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setSelectedSiteId(siteId);
  }, [siteId]);

  useEffect(() => {
    if (selectedSiteId < 1) {
      setTypeOptions([]);
      return;
    }
    let cancelled = false;
    setTypesLoading(true);
    void (async () => {
      try {
        const r = await apiFetch<{ ok: boolean; items?: SiteMasterEntryTypeOption[] }>(
          'site_master_list_entry_types',
          { method: 'GET', query: { site_id: selectedSiteId } }
        );
        if (cancelled) return;
        if (r.ok && Array.isArray(r.items)) {
          setTypeOptions(r.items);
          if (initialTypeKey && r.items.some((t) => t.key === initialTypeKey)) {
            setTypeKey(initialTypeKey);
          } else if (r.items.length === 1) {
            setTypeKey(r.items[0].key);
          }
        } else {
          setTypeOptions([]);
        }
      } catch {
        if (!cancelled) setTypeOptions([]);
      } finally {
        if (!cancelled) setTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId, initialTypeKey]);

  async function uploadImages(files: FileList | null) {
    if (!files?.length) return;
    if (selectedSiteId < 1) {
      setErr('请选择站点');
      return;
    }
    const remain = SITE_MASTER_IMAGE_MAX_COUNT - imagePaths.length;
    if (remain < 1) {
      setErr(`最多上传 ${SITE_MASTER_IMAGE_MAX_COUNT} 张图片`);
      return;
    }
    setUploadBusy(true);
    setErr(null);
    const next: string[] = [];
    try {
      const batch = Array.from(files).slice(0, remain);
      for (const file of batch) {
        if (file.size > SITE_MASTER_IMAGE_MAX_BYTES) {
          throw new Error(`「${file.name}」超过单张 5MB，请先压缩`);
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('site_id', String(selectedSiteId));
        const res = await fetch(apiUrl('site_master_upload_entry_image'), {
          method: 'POST',
          body: fd,
          credentials: 'include',
        });
        const raw = await res.text();
        const text = raw.replace(/^\uFEFF/, '').trim();
        let parsed: { ok?: boolean; err?: string; detail?: string; local_path?: string };
        try {
          parsed = JSON.parse(text) as typeof parsed;
        } catch {
          throw new Error(text.slice(0, 120) || '上传响应无效');
        }
        if (parsed.err === 'unauthorized' && typeof window !== 'undefined') {
          window.location.replace(siteMasterLoginUrl());
          return;
        }
        if (!parsed.ok || !parsed.local_path) {
          const e = parsed.err || '上传失败';
          throw new Error(e === 'file_too_large' ? '单张图片不能超过 5MB' : parsed.detail ?? e);
        }
        next.push(parsed.local_path);
      }
      setImagePaths((prev) => [...prev, ...next]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function removeImage(path: string) {
    setImagePaths((prev) => prev.filter((p) => p !== path));
  }

  function handleSubmit() {
    if (siteOptions && siteOptions.length > 1 && selectedSiteId < 1) {
      setErr('请选择站点');
      return;
    }
    if (typeOptions.length === 0) {
      setErr('本站点尚未配置物品类型，请联系管理员在小程序中设置');
      return;
    }
    if (!typeKey) {
      setErr('请选择物品类型');
      return;
    }
    const b = body.trim();
    if (b === '') {
      setErr('请填写物品信息');
      return;
    }
    const p = phone.trim();
    const w = wechatId.trim();
    if (p === '' && w === '') {
      setErr('请至少填写联系电话或微信号（ID）');
      return;
    }
    if (w !== '' && !wechatIdLooksValid(w)) {
      setErr('微信号格式不正确（4-32 位字母、数字、下划线或连字符）');
      return;
    }
    setErr(null);
    void onSubmit({
      ...(siteOptions && siteOptions.length > 1 ? { site_id: selectedSiteId } : {}),
      type_key: typeKey,
      body_text: b,
      contact_phone: p,
      contact_wechat_id: w,
      image_local_paths: imagePaths,
    });
  }

  const busy = saving || uploadBusy || typesLoading;
  const canAddMore = imagePaths.length < SITE_MASTER_IMAGE_MAX_COUNT;
  const typesReady = !typesLoading && typeOptions.length > 0;
  const hasPhone = phone.trim() !== '';
  const hasWechat = wechatId.trim() !== '';
  const contactOk = hasPhone || hasWechat;

  return (
    <div className="space-y-5">
      {err ? (
        <p className="rounded-2xl border-2 border-red-200 bg-red-50 px-4 py-3 text-lg text-red-900" role="alert">
          {err}
        </p>
      ) : null}

      {siteOptions && siteOptions.length > 1 ? (
        <label className="block">
          <span className={labelClass}>
            所属站点
            <span className="ml-1 text-base font-normal text-red-600">*</span>
          </span>
          <select
            className={cn(fieldClass, 'appearance-none bg-white')}
            value={selectedSiteId || ''}
            onChange={(e) => setSelectedSiteId(parseInt(e.target.value, 10) || 0)}
          >
            <option value="">请选择站点</option>
            {siteOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.site_name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="block">
        <span className={labelClass}>
          物品类型
          <span className="ml-1 text-base font-normal text-red-600">*</span>
        </span>
        {typesLoading ? (
          <p className="rounded-2xl border-2 border-slate-200 bg-slate-50 px-4 py-3.5 text-lg text-slate-500">加载类型…</p>
        ) : typeOptions.length === 0 ? (
          <p className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-3.5 text-base leading-relaxed text-amber-950">
            本站点尚未配置物品类型。请管理员在「小程序管理」中为覆盖本站点的小程序配置信息类型后再发布。
          </p>
        ) : (
          <select
            className={cn(fieldClass, 'appearance-none bg-white pr-10')}
            value={typeKey}
            onChange={(e) => setTypeKey(e.target.value)}
            aria-required
          >
            <option value="">请选择物品类型</option>
            {typeOptions.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        )}
      </label>

      <label className="block">
        <span className={labelClass}>
          物品信息
          <span className="ml-1 text-base font-normal text-red-600">*</span>
        </span>
        <textarea
          className={cn(fieldClass, 'min-h-[10rem] resize-y leading-relaxed')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="请描述物品情况"
          required
        />
      </label>

      <div>
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className={labelClass}>物品图片</span>
          <span className="text-base text-slate-500">
            {imagePaths.length}/{SITE_MASTER_IMAGE_MAX_COUNT} 张
          </span>
        </div>
        <p className="mb-3 text-base text-slate-500">可选，单张不超过 5MB，支持一次选多张</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => void uploadImages(e.target.files)}
        />
        {canAddMore ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 py-4 text-lg font-semibold text-slate-700 disabled:opacity-50"
          >
            <i className="fa-solid fa-images text-xl" aria-hidden />
            {uploadBusy ? '上传中…' : '选择图片'}
          </button>
        ) : null}
        {imagePaths.length > 0 ? (
          <ul className="mt-3 grid grid-cols-3 gap-2">
            {imagePaths.map((p) => (
              <li key={p} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                <img src={resolveMediaUrl(p)} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-lg font-bold text-white"
                  aria-label="移除图片"
                  onClick={() => removeImage(p)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <section
        className={cn(
          'rounded-2xl border-2 p-4 transition-colors',
          contactOk ? 'border-slate-200 bg-white' : 'border-amber-200 bg-amber-50/40'
        )}
        aria-labelledby="site-master-contact-heading"
      >
        <div className="mb-4">
          <h3 id="site-master-contact-heading" className="text-lg font-semibold text-slate-800">
            联系方式
            <span className="ml-1 text-base font-normal text-red-600">*</span>
          </h3>
          <p className="mt-1 text-base text-slate-500">联系电话与微信号（ID）至少填写一项，可同时填写</p>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-base font-medium text-slate-700">
              <i className="fa-solid fa-phone text-teal-600" aria-hidden />
              联系电话
            </span>
            <input
              className={fieldClass}
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="选填，便于买家直接拨打"
              autoComplete="tel"
            />
          </label>
          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-base font-medium text-slate-700">
              <i className="fa-brands fa-weixin text-emerald-600" aria-hidden />
              微信号（ID）
            </span>
            <input
              className={fieldClass}
              type="text"
              value={wechatId}
              onChange={(e) => setWechatId(e.target.value)}
              placeholder="选填，无电话时可仅填微信号"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </label>
        </div>
      </section>

      <button
        type="button"
        disabled={busy || !typesReady}
        onClick={handleSubmit}
        className="w-full rounded-2xl bg-teal-600 py-4 text-xl font-bold text-white shadow-lg disabled:opacity-60"
      >
        {saving ? '保存中…' : submitLabel}
      </button>

      {footer}
    </div>
  );
}
